import { useEffect, useRef } from 'react';
import { SUGGESTION_LABEL, SUGGESTION_TRIGGER_CLASS } from '../../core/suggestions';

export interface SuggestionBoxProps {
  /** PostHog is running the survey. False keeps the button off the page entirely. */
  available: boolean;
  /** Reported when the button is clicked, before PostHog opens its form. */
  onOpen: () => void;
}

/**
 * The button in the corner. That is the whole component — the form that opens above it
 * is rendered by posthog-js, in its own shadow root, from the survey definition.
 *
 * Nothing here opens anything. posthog-js polls the page once a second, finds the
 * element matching the survey's `widgetSelector`, and attaches its own click listener.
 * A button that rendered before the survey loaded is picked up on the next poll, and
 * one whose element React later swapped is re-attached the same way.
 */
export default function SuggestionBox({ available, onOpen }: SuggestionBoxProps) {
  const button = useRef<HTMLButtonElement>(null);
  const handler = useRef(onOpen);
  handler.current = onOpen;

  /**
   * A native listener, not React's `onClick`.
   *
   * PostHog's listener sits on this element and calls `stopPropagation()` so the click
   * does not reach the page behind it. React 19 delegates `onClick` to the root
   * container, which is an ancestor — so a React handler here would never run, and the
   * event would be lost exactly when someone used the feature. `stopPropagation` does
   * not affect other listeners on the element itself, so this one fires either way.
   */
  useEffect(() => {
    const element = button.current;
    if (!element) return;

    const report = () => handler.current();
    element.addEventListener('click', report);
    return () => element.removeEventListener('click', report);
  }, [available]);

  if (!available) return null;

  return (
    <button
      ref={button}
      type="button"
      className={`suggestion-box ${SUGGESTION_TRIGGER_CLASS}`}
      aria-haspopup="dialog"
    >
      <svg
        className="suggestion-box-icon"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M12 7v6" />
        <path d="M9 10h6" />
      </svg>
      {SUGGESTION_LABEL}
    </button>
  );
}
